/**
 * DEV-329 — Чат-комментарии к товару (spec chat).
 * Разметка — adm_post_list / adm_post_item / adm_post_sender (тот же стиль, что
 * на странице комментариев проекта resources/views/user/projectcomments.blade.php).
 */

import { apiRequest } from '../../api.js';

const state = {
    projectId: null,
    currentUserId: null,
};

export function initSpecChat(projectId, currentUserId) {
    state.projectId = projectId;
    state.currentUserId = parseInt(currentUserId, 10) || null;
    if (state.bound) return;
    document.addEventListener('click', onSendClick);
    document.addEventListener('input', onInput);
    document.addEventListener('keydown', onKeydown);
    state.bound = true;
}

async function onSendClick(e) {
    const btn = e.target.closest('.spec-chat__send');
    if (!btn) return;
    e.preventDefault();
    const chat = btn.closest('[data-spec-chat]');
    if (!chat) return;
    await sendMessage(chat);
}

async function sendMessage(chat) {
    const specId = chat.dataset.specId;
    const input = chat.querySelector('[data-spec-chat-input]');
    const message = (input?.value || '').trim();
    if (!message || !state.projectId || !specId) return;
    const sendBtn = chat.querySelector('.spec-chat__send');
    if (sendBtn) sendBtn.disabled = true;
    try {
        const data = await apiRequest(`/project/${state.projectId}/spec/${specId}/chat`, {
            method: 'POST',
            body: { message },
        });
        if (data?.success) {
            await openSpecChat(specId);
            if (input) { input.value = ''; input.parentNode.dataset.value = ''; }
        }
    } catch (err) {
        console.error('spec-chat send error', err);
        if (window.showErrorToast) window.showErrorToast(err.message || 'Failed to send message');
    } finally {
        if (sendBtn) sendBtn.disabled = !input?.value.trim();
    }
}

/**
 * Открыть чат для spec — рендер списка сообщений + markRead.
 */
export async function openSpecChat(specId) {
    if (!state.projectId || !specId) return;

    const containers = findChatContainers(specId);
    if (!containers.length) return;

    try {
        const data = await apiRequest(`/project/${state.projectId}/spec/${specId}/chat`);
        if (!data?.success) return;
        containers.forEach(c => renderMessages(c, data.messages));

        setIconHasMessages(specId, (data.messages || []).length > 0);

        await apiRequest(`/project/${state.projectId}/spec/${specId}/chat/read`, { method: 'POST' });
        setUnreadBadge(specId, 0);
    } catch (e) {
        console.error('spec-chat load error', e);
    }
}

function findChatContainers(specId) {
    return Array.from(document.querySelectorAll(`[data-spec-chat][data-spec-id="${specId}"]`));
}

function renderMessages(container, messages) {
    const list = container.querySelector('[data-spec-chat-messages]');
    if (!list) return;

    if (!messages || messages.length === 0) {
        list.innerHTML = '<div class="spec-chat__empty" data-spec-chat-empty>No messages sent yet</div>';
        return;
    }

    list.innerHTML = messages.map(renderMessage).join('');
    list.scrollTop = list.scrollHeight;
}

function renderMessage(m) {
    const isOwn = !!m.is_own;
    const sideMod = isOwn ? 'spec-chat__msg--own' : 'spec-chat__msg--other';
    const author = isOwn ? 'YOU' : escapeHtml(String(m.user_name || '').toUpperCase());
    const dateStr = formatDate(m.created_at) + (m.created_at_human ? ', ' + escapeHtml(m.created_at_human.slice(11, 16)) : '');
    const body = escapeHtml(m.message);

    return `<div class="spec-chat__msg ${sideMod}" data-msg-id="${m.id}">
        <div class="spec-chat__msg-header">
            <div class="spec-chat__msg-wrap">
                <div class="spec-chat__msg-title">${author}</div>
                <div class="spec-chat__msg-date">${dateStr}</div>
            </div>
        </div>
        <div class="spec-chat__msg-body">
            <div class="spec-chat__msg-wrap">
                <div class="spec-chat__msg-text">${body}</div>
            </div>
        </div>
    </div>`;
}

function formatDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const weekday = ['SUNDAY','MONDAY','TUESDAY','WEDNESDAY','THURSDAY','FRIDAY','SATURDAY'][d.getDay()];
    const month = ['JANUARY','FEBRUARY','MARCH','APRIL','MAY','JUNE','JULY','AUGUST','SEPTEMBER','OCTOBER','NOVEMBER','DECEMBER'][d.getMonth()];
    return `${weekday}, ${month} ${d.getDate()}, ${d.getFullYear()}`;
}

function onInput(e) {
    const input = e.target.closest('[data-spec-chat-input]');
    if (!input) return;
    const chat = input.closest('[data-spec-chat]');
    if (!chat) return;
    const sendBtn = chat.querySelector('.spec-chat__send');
    if (sendBtn) sendBtn.disabled = !input.value.trim();
}

function onKeydown(e) {
    if (e.key !== 'Enter' || e.shiftKey) return;
    const input = e.target.closest('[data-spec-chat-input]');
    if (!input) return;
    const chat = input.closest('[data-spec-chat]');
    if (!chat) return;
    e.preventDefault();
    sendMessage(chat);
}

export function setUnreadBadge(specId, count) {
    const safeCount = Math.max(0, parseInt(count, 10) || 0);
    document
        .querySelectorAll(`[data-spec_comment_handler][data-spec-id="${specId}"], [data-mobile_comment_handler_id="${specId}"]`)
        .forEach(el => {
            el.dataset.unreadCount = String(safeCount);
            el.classList.toggle('active', safeCount > 0);
        });
}

/**
 * Показать/скрыть «полоски текста» в иконке облачка (есть ли сообщения в треде).
 */
export function setIconHasMessages(specId, hasMessages) {
    document
        .querySelectorAll(`[data-spec_comment_handler][data-spec-id="${specId}"], [data-mobile_comment_handler_id="${specId}"]`)
        .forEach(el => {
            el.querySelectorAll('.adm_spec_tool_comment_lines').forEach(line => {
                line.style.display = hasMessages ? '' : 'none';
            });
        });
}

export function applyUnreadCounts(countsMap, hasMessagesList) {
    if (!countsMap || typeof countsMap !== 'object') return;
    document.querySelectorAll('[data-spec_comment_handler][data-spec-id], [data-mobile_comment_handler_id]')
        .forEach(el => {
            const id = el.dataset.specId || el.dataset.mobile_comment_handler_id;
            if (id) setUnreadBadge(id, 0);
        });
    Object.entries(countsMap).forEach(([specId, cnt]) => setUnreadBadge(specId, cnt));

    if (Array.isArray(hasMessagesList)) {
        const set = new Set(hasMessagesList.map(String));
        document.querySelectorAll('[data-spec_comment_handler][data-spec-id], [data-mobile_comment_handler_id]')
            .forEach(el => {
                const id = el.dataset.specId || el.dataset.mobile_comment_handler_id;
                if (id) setIconHasMessages(id, set.has(String(id)));
            });
    }
}

function escapeHtml(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
